import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Header,
  SpaceBetween,
  ContentLayout,
  Container,
  FormField,
  Input,
  Textarea,
  ColumnLayout,
  Box,
  Flashbar,
  Tabs,
  Link,
} from "@cloudscape-design/components";
import { TrajectoryLoader } from "../common/Loaders";
import CodeEditor from "../common/CodeEditor";
import { useCapabilitiesStore } from "src/store/capability-store";
import { useNotificationStore } from "src/store/notification-store";
import { ROUTES } from "src/constants";
import { Button } from "../common/Button/Button";

interface FormData {
  name: string;
  description: string;
  tools: { value: string }[];
  instruction: string;
  inputSchema: string;
  input: string;
  output: string;
  outputSchema: string;
  reportSectionTemplate: string;
}

// Helper component for format JSON links
const FormatJsonLink: React.FC<{ onFormat: () => void }> = ({ onFormat }) => (
  <Link variant="info" onFollow={onFormat}>
    Format JSON
  </Link>
);

const CapabilityEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    capabilities,
    isLoading,
    isUpdating,
    fetchCapabilities,
    updateCapability,
  } = useCapabilitiesStore();
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      tools: [{ value: "" }],
      instruction: "",
      inputSchema: "",
      input: "",
      output: "",
      outputSchema: "",
      reportSectionTemplate: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "tools",
  });

  const { error: showError, success: showSuccess } = useNotificationStore();
  const [capabilityNotFound, setCapabilityNotFound] = React.useState(false);
  const [activeTabId, setActiveTabId] = useState("details");

  // Helper function to format JSON
  const formatJson = (fieldName: keyof FormData) => {
    const value = watch(fieldName) as string;

    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      setValue(fieldName, formatted);
    } catch (error) {
      showError("Invalid JSON - cannot format. Please fix syntax errors first.");
    }
  };

  // Fetch capabilities on component mount
  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  useEffect(() => {
    if (id && capabilities?.capabilities) {
      const selectedCap = capabilities.capabilities.find(
        (cap: any) => cap.id === id
      );
      if (selectedCap) {
        reset({
          name: selectedCap.name || "",
          description: selectedCap.description || "",
          instruction: selectedCap.instruction || "",
          inputSchema: selectedCap.inputSchema
            ? JSON.stringify(selectedCap.inputSchema, null, 2)
            : "",
          input: selectedCap.input
            ? JSON.stringify(selectedCap.input, null, 2)
            : "",
          output: selectedCap.output
            ? JSON.stringify(selectedCap.output, null, 2)
            : "",
          outputSchema: selectedCap.outputSchema
            ? JSON.stringify(selectedCap.outputSchema, null, 2)
            : "",
          reportSectionTemplate: selectedCap.reportSectionTemplate || "",
          tools:
            Array.isArray(selectedCap.tools) && selectedCap.tools.length > 0
              ? selectedCap.tools.map((tool: string) => ({ value: tool }))
              : [{ value: "" }],
        });
        setCapabilityNotFound(false);
      } else {
        setCapabilityNotFound(true);
      }
    }
  }, [id, capabilities, reset]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    if (!id) return;

    try {
      const safeJsonParse = (jsonString: string, fieldName: string) => {
        if (!jsonString.trim()) return null;
        try {
          return JSON.parse(jsonString);
        } catch (error) {
          showError(
            `Invalid JSON in field: ${fieldName}. Please check your syntax.`
          );
        }
      };

      const updateData = {
        capabilityId: id,
        capability: {
          name: data.name,
          description: data.description,
          tools: data.tools
            .map((tool) => tool.value)
            .filter((tool) => tool.trim() !== ""),
          instruction: data.instruction,
          inputSchema: safeJsonParse(data.inputSchema, "Input Schema"),
          input: safeJsonParse(data.input, "Input"),
          output: safeJsonParse(data.output, "Output"),
          outputSchema: safeJsonParse(data.outputSchema, "Output Schema"),
          reportSectionTemplate: data.reportSectionTemplate,
        },
      };

      await updateCapability(id, updateData);

      showSuccess("Capability updated successfully", "Success");

      navigationTimeoutRef.current = setTimeout(() => {
        navigate(ROUTES.CAPABILITIES);
      }, 1500);
    } catch (error: any) {
      showError("Failed to Update Capability: "+error.message)
    }
  };

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">Loading capability...</div>
        </div>
      </ContentLayout>
    );
  }

  if (!isLoading && capabilityNotFound) {
    return (
      <ContentLayout defaultPadding>
        <Box textAlign="center">
          <SpaceBetween direction="vertical" size="m">
            <Header variant="h2">Capability Not Found</Header>
            <Button href={ROUTES.CAPABILITIES}>
              Back to Capabilities
            </Button>
          </SpaceBetween>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description="Edit capability settings and tools"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="secondary"
                href={ROUTES.CAPABILITIES}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSubmit(onSubmit)()}
                loading={isUpdating}
                disabled={!id}
              >
                Update
              </Button>
            </SpaceBetween>
          }
        >
          {watch("name")
            ? `Edit Capability - ${watch("name")}`
            : "Edit Capability"}
        </Header>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SpaceBetween direction="vertical" size="l">
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
            tabs={[
              {
                id: "details",
                label: "Details",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <FormField
                        label={
                          <Box fontSize="heading-m" fontWeight="bold">
                            Capability Name
                          </Box>
                        }
                        description="A unique identifier for this capability"
                        stretch
                      >
                        <Input
                          value={watch("name")}
                          onChange={({ detail }) =>
                            setValue("name", detail.value)
                          }
                          placeholder="Enter capability name"
                        />
                      </FormField>

                      <FormField
                        label={
                          <Box fontSize="heading-m" fontWeight="bold">
                            Description
                          </Box>
                        }
                        description="Describe what this capability does"
                        stretch
                      >
                        <Textarea
                          value={watch("description")}
                          onChange={({ detail }) =>
                            setValue("description", detail.value)
                          }
                          rows={4}
                          placeholder="Enter description"
                        />
                      </FormField>

                      <FormField
                        label={
                          <Box fontSize="heading-m" fontWeight="bold">
                            Tools
                          </Box>
                        }
                        description="List of tools available to this capability"
                        stretch
                      >
                        <SpaceBetween direction="vertical" size="s">
                          {fields.map((field, index) => (
                            <ColumnLayout
                              key={field.id}
                              columns={2}
                              variant="text-grid"
                            >
                              <Input
                                value={watch(`tools.${index}.value`)}
                                onChange={({ detail }) =>
                                  setValue(`tools.${index}.value`, detail.value)
                                }
                                placeholder="Tool name"
                              />
                              <Button
                                variant="secondary"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                Remove
                              </Button>
                            </ColumnLayout>
                          ))}
                          <Box textAlign="left">
                            <Button
                              variant="secondary"
                              onClick={() => append({ value: "" })}
                            >
                              Add Tool
                            </Button>
                          </Box>
                        </SpaceBetween>
                      </FormField>
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                id: "instruction",
                label: "Instruction",
                content: (
                  <Container>
                    <FormField
                      label={
                        <Box fontSize="heading-m" fontWeight="bold">
                          Instruction
                        </Box>
                      }
                      description="Define how this capability should be executed"
                      stretch
                    >
                      <Box>
                        <CodeEditor
                          value={watch("instruction")}
                          onChange={(value) => setValue("instruction", value)}
                          language="markdown"
                          height="500px"
                          placeholder="Enter instruction in Markdown format..."
                        />
                      </Box>
                    </FormField>
                  </Container>
                ),
              },
              {
                id: "input",
                label: "Input",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box fontSize="heading-m" fontWeight="bold">
                              Input Schema
                            </Box>
                            <FormatJsonLink
                              onFormat={() => formatJson("inputSchema")}
                            />
                          </SpaceBetween>
                        }
                        description="Define the JSON schema for input validation"
                        stretch
                      >
                        <Box>
                          <CodeEditor
                            value={watch("inputSchema")}
                            onChange={(value) => setValue("inputSchema", value)}
                            language="json"
                            height="350px"
                            placeholder="Enter JSON schema..."
                          />
                        </Box>
                      </FormField>

                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box fontSize="heading-m" fontWeight="bold">
                              Input
                            </Box>
                            <FormatJsonLink
                              onFormat={() => formatJson("input")}
                            />
                          </SpaceBetween>
                        }
                        description="Provide capabilities input that conforms to the schema"
                        stretch
                      >
                        <Box>
                          <CodeEditor
                            value={watch("input")}
                            onChange={(value) => setValue("input", value)}
                            language="json"
                            height="250px"
                            placeholder="Enter JSON input..."
                          />
                        </Box>
                      </FormField>
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                id: "output",
                label: "Output",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box fontSize="heading-m" fontWeight="bold">
                              Output Schema
                            </Box>
                            <FormatJsonLink
                              onFormat={() => formatJson("outputSchema")}
                            />
                          </SpaceBetween>
                        }
                        description="Define the JSON schema for output validation"
                        stretch
                      >
                        <Box>
                          <CodeEditor
                            value={watch("outputSchema")}
                            onChange={(value) =>
                              setValue("outputSchema", value)
                            }
                            language="json"
                            height="350px"
                            placeholder="Enter JSON schema..."
                          />
                        </Box>
                      </FormField>

                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box fontSize="heading-m" fontWeight="bold">
                              Output
                            </Box>
                            <FormatJsonLink
                              onFormat={() => formatJson("output")}
                            />
                          </SpaceBetween>
                        }
                        description="Provide capabilities output that conforms to the schema"
                        stretch
                      >
                        <Box>
                          <CodeEditor
                            value={watch("output")}
                            onChange={(value) => setValue("output", value)}
                            language="json"
                            height="250px"
                            placeholder="Enter JSON output..."
                          />
                        </Box>
                      </FormField>
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                id: "report",
                label: "Report Template",
                content: (
                  <Container>
                    <FormField
                      label={
                        <Box fontSize="heading-m" fontWeight="bold">
                          Report Section Template
                        </Box>
                      }
                      description="Define how results should be formatted in reports"
                      stretch
                    >
                      <Box>
                        <CodeEditor
                          value={watch("reportSectionTemplate")}
                          onChange={(value) =>
                            setValue("reportSectionTemplate", value)
                          }
                          language="vtl"
                          height="500px"
                          placeholder="Enter report section template..."
                        />
                      </Box>
                    </FormField>
                  </Container>
                ),
              },
            ]}
          />
        </SpaceBetween>
      </form>
    </ContentLayout>
  );
};

export default CapabilityEditPage;
